import type { Paragraph, TextRun } from '@/api/types/stowed-element'
import { DEFAULT_POINT_SIZE } from '@/lib/text/constants/text-defaults'
import { clampDropCapLines, clampDropCapOffset, normalizeDropCapValue } from '@/lib/text/drop-cap'
import {
  measureFontAscentDescentPt,
  measureGlyphAdvanceWidthPt,
  measureGlyphInkMetricsPt,
  solveDropCapFontSizePt,
} from '@/lib/text/canvas-font-metrics'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import { parsedLeadingPt } from '@/lib/text/leading'
import type { DropCapPlacement } from '../types/drop-cap-placement'
import type { DropCapFit } from '../types/drop-cap-fit'

export function isBreakCharacter(ch: string): boolean {
  return ch === ' ' || ch === '\t'
}

/**
 * Flat-text `[start, end)` range for each paragraph, in `paragraphsToPlainText`'s own indexing:
 * paragraphs joined by a single `\n`, each paragraph's own lines concatenated with no separator.
 */
export function paragraphFlatRanges(
  paragraphs: Paragraph[]
): Array<{ start: number; end: number }> {
  const ranges: Array<{ start: number; end: number }> = []
  let offset = 0

  paragraphs.forEach((paragraph, i) => {
    if (i > 0) offset += 1 // the '\n' separator `paragraphsToPlainText` joins with

    const len = (paragraph.textLines ?? [])
      .flatMap((line) => line.textRuns ?? [])
      .reduce((sum, run) => sum + (run.textContent ?? '').length, 0)

    ranges.push({ start: offset, end: offset + len })
    offset += len
  })

  return ranges
}

/**
 * Finds every paragraph with an active drop cap (Inside or Outside — mutual exclusivity is already
 * structural, since `dropCap` is one wire enum, never two independent flags) and resolves it to a
 * placement. A paragraph whose text is empty, or whose very first character is a break space,
 * produces no placement — there is no glyph to cap (leading whitespace inside a paragraph is a
 * documented simplification, not something this reflows past to find the first non-space
 * character).
 */
export function computeDropCapPlacements(
  paragraphs: Paragraph[],
  text: string,
  charRunMap: Array<TextRun | null>
): DropCapPlacement[] {
  const ranges = paragraphFlatRanges(paragraphs)
  const placements: DropCapPlacement[] = []

  paragraphs.forEach((paragraph, paragraphIndex) => {
    const wireValue = normalizeDropCapValue(paragraph.dropCap)

    if (wireValue === 'NONE') return

    const { start, end } = ranges[paragraphIndex]

    if (start >= end) return

    const character = text[start]

    if (!character || isBreakCharacter(character)) return

    const run = charRunMap[start] ?? null
    const pointSize = run?.pointSize ?? DEFAULT_POINT_SIZE

    placements.push({
      paragraphIndex,
      charIndex: start,
      paragraphEndIndex: end,
      character,
      style: wireValue,
      lines: clampDropCapLines(paragraph.dropCapLines),
      offsetPt: clampDropCapOffset(paragraph.dropCapOffset),
      pointSize,
      ownRowHeightPt: parsedLeadingPt(run?.leading, pointSize),
      fontFamilyId: run?.fontFamilyId,
      fontId: run?.fontId,
    })
  })

  return placements
}

/**
 * Solves each placement's fitted font size, resulting indent, and vertical alignment. Independent
 * of any row narrowing — sizing depends only on Lines, the paragraph's OWN row pitch, the character,
 * and its font, never on how the affected rows get narrowed.
 *
 * The height target reduces to a clean `(lines - 1) * ownRowHeightPt + bodyAscentPt`: writing out
 * "top of the body text on the cap's own row" as `rowTop + baselineOffsetPt - bodyAscentPt` and
 * "baseline of the row `lines - 1` further down" as
 * `rowTop + (lines - 1) * ownRowHeightPt + baselineOffsetPt` (every row of THIS paragraph shares one
 * pitch, so this needs no absolute row index), the `rowTop` and `baselineOffsetPt` terms cancel in
 * the difference — leaving a formula that needs only `lines`, this paragraph's own row pitch, and
 * the body run's own ascent, computable before any layout has run at all (needed up front:
 * `layoutShapeTextWithDropCap`'s `dropCapOverrides` require `indentPt`, which requires `fontSizePt`,
 * before the layout pass that would otherwise discover the cap's absolute row).
 *
 * Deliberately uses `placement.ownRowHeightPt`, not a `flow.rowHeight`-style frame-wide value: see
 * that field's own doc comment for why the frame-wide probe over-sizes the cap.
 */
export function solveDropCapFits(
  placements: readonly DropCapPlacement[],
  ctx: CanvasRenderingContext2D | null,
  fonts: ParagraphBlockFontLookup | undefined
): DropCapFit[] {
  return placements.map((placement) => {
    const { ascentPt: bodyAscentPt } = measureFontAscentDescentPt(
      ctx,
      placement.pointSize,
      placement.fontFamilyId,
      placement.fontId,
      fonts
    )
    const inkHeightPt = (placement.lines - 1) * placement.ownRowHeightPt + bodyAscentPt
    const { fontSizePt } = solveDropCapFontSizePt(
      ctx,
      placement.character,
      inkHeightPt,
      placement.fontFamilyId,
      placement.fontId,
      fonts
    )
    const capWidthPt = measureGlyphAdvanceWidthPt(
      ctx,
      placement.character,
      fontSizePt,
      placement.fontFamilyId,
      placement.fontId,
      fonts
    )
    const { inkAscentPt } = measureGlyphInkMetricsPt(
      ctx,
      placement.character,
      fontSizePt,
      placement.fontFamilyId,
      placement.fontId,
      fonts
    )
    const { ascentPt: capFontBoxAscentPt } = measureFontAscentDescentPt(
      ctx,
      fontSizePt,
      placement.fontFamilyId,
      placement.fontId,
      fonts
    )

    return {
      ...placement,
      fontSizePt,
      capWidthPt,
      indentPt: capWidthPt + placement.offsetPt,
      bodyAscentPt,
      capTopInsetPt: capFontBoxAscentPt - inkAscentPt,
      inkHeightPt,
      capFontBoxAscentPt,
    }
  })
}
